/* eslint-disable no-magic-numbers */
import { sanitiseProductId } from '@shared/helpers/shopify';
import { IProductResult } from '@unified-commerce/gpc-vue-storefront-search';
import { cartGetters, Product } from '@unified-commerce/gpc-vue-storefront-shopify';
import { Cart } from '@unified-commerce/gpc-vue-storefront-shopify/api-client/src/types';

type EventName =
  | 'view_cart'
  | 'cart_update'
  | 'product_view'
  | 'product_category_view'
  | 'search_result_page_view'
  | 'add_to_cart'
  | 'select_item'
  | 'view_item_list'
  | 'view_item'
  | 'Hits Viewed'
  | 'jsonld_validation_error';
type ContentType = 'product';
type Currency = 'AUD';

interface Item {
  item_id: string;
  item_name?: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

export interface ShopifyProduct extends Partial<Product> {
  variantId?: string;
  name?: string;
  vendor?: string;
  link?: string;
}

interface AnalyticsEvent extends Partial<Item> {
  event: EventName;
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: ContentType;
  value?: number;
  currency?: Currency;
  discount?: number;
  items?: Item[];
  customer_id?: string;
  objectId?: string;
  position?: number;
  queryId?: string;
}

interface CartUpdateAnalyticsEvent extends AnalyticsEvent {
  cart_items?: Item[];
}

interface AddToCartPayload {
  event: 'add_to_cart';
  id?: string;
  name?: string;
  price?: number;
  vendor?: string;
  category?: string;
  customer_id?: string;
  objectId?: string;
}

interface ViewCartPayload {
  event: 'view_cart' | 'cart_update';
  cart?: Cart | null;
  customer_id?: string;
}

interface ItemPayload {
  event: 'view_item' | 'select_item';
  product?: ShopifyProduct;
  discount?: number;
  price?: number;
  category?: string;
  customer_id?: string;
  objectId?: string;
  position?: number;
  queryId?: string;
}

interface ViewItemListPayload {
  event: 'view_item_list';
  id?: string;
  name?: string;
  category?: string;
  items?: IProductResult[];
  customer_id?: string;
}

interface HitsViewedEvent {
  event: 'Hits Viewed';
  index?: string;
  objectIDs?: string[];
}

interface JsonLdValidationErrorPayload {
  event: 'jsonld_validation_error';
  jsonld_type: string;
  brand: string;
  page_url: string;
  issue_count: number;
  issues: string;
}

type HitsViewedPayload = HitsViewedEvent;
type HitsViewedAnalyticsEvent = HitsViewedEvent;
type JsonLdValidationErrorEvent = JsonLdValidationErrorPayload;

type AnalyticsEventPayload =
  | ViewCartPayload
  | ViewItemListPayload
  | AddToCartPayload
  | ItemPayload
  | HitsViewedPayload
  | JsonLdValidationErrorEvent;

const getAddToCardPayload = (payload: AddToCartPayload): AnalyticsEvent => {
  return {
    event: payload.event,
    content_type: 'product',
    currency: 'AUD',
    items: [
      {
        item_brand: payload.vendor ?? '',
        item_category: payload.category ?? '',
        item_id: sanitiseProductId(payload.id),
        item_name: payload.name ?? '',
        price: payload.price ?? 0,
      },
    ],
    customer_id: payload?.customer_id ?? '',
    objectId: sanitiseProductId(payload?.objectId || ''),
  };
};

const getViewCartPayload = (payload: ViewCartPayload): AnalyticsEvent => {
  return {
    event: payload.event,
    currency: 'AUD',
    value: cartGetters.getTotals(payload?.cart)?.subtotal ?? 0,
    items: payload?.cart
      ? cartGetters.getItems(payload.cart)?.map((item) => ({
          item_id: sanitiseProductId(item?.id ?? ''),
          item_name: item?.title ?? '',
          item_brand: cartGetters.getVendor(item) ?? '',
          item_variant: item?.id ?? '',
          price: cartGetters.getPrice(item) ?? 0,
          quantity: item?.quantity ?? 0,
        }))
      : [],
    customer_id: payload?.customer_id ?? '',
  };
};

const getUpdateCartPayload = (payload: ViewCartPayload): CartUpdateAnalyticsEvent => {
  return {
    event: payload.event,
    currency: 'AUD',
    value: cartGetters.getTotals(payload?.cart)?.subtotal ?? 0,
    cart_items: payload?.cart
      ? cartGetters.getItems(payload.cart)?.map((item) => ({
          item_id: sanitiseProductId(item?.id ?? ''),
          item_name: item?.title ?? '',
          item_brand: cartGetters.getVendor(item) ?? '',
          item_variant: item?.id ?? '',
          price: cartGetters.getPrice(item) ?? 0,
          quantity: item?.quantity ?? 0,
        }))
      : [],
    customer_id: payload?.customer_id ?? '',
  };
};

const getItemPayload = (payload: ItemPayload): AnalyticsEvent => {
  return {
    event: payload.event,
    currency: 'AUD',
    value: payload.price,
    items: [
      {
        discount: payload.discount,
        item_brand: payload.product?.vendor ?? '',
        item_category: payload?.category ?? '',
        item_id: sanitiseProductId(payload.product?.id),
        item_list_id: 'product',
        item_list_name: 'Product',
        item_name: payload.product?.name,
        price: payload.price,
      },
    ],
    customer_id: payload?.customer_id ?? '',
    objectId: payload.objectId,
    position: payload.position,
    queryId: payload.queryId,
  };
};

const getViewItemListPayload = (payload: ViewItemListPayload): AnalyticsEvent => {
  return {
    event: payload.event,
    item_list_id: payload.id,
    item_list_name: payload.name,
    items: payload.items?.map(({ record }) => ({
      item_id: sanitiseProductId(record?.id),
      item_name: record?.title ?? '',
      item_brand: record?.vendor ?? '',
      item_category: payload.category ?? '',
      currency: 'AUD',
    })),
    customer_id: payload?.customer_id ?? '',
  };
};

const getHitsViewedPayload = (payload: HitsViewedPayload): HitsViewedAnalyticsEvent => {
  return payload;
};

const getJsonLdValidationPayload = (
  payload: JsonLdValidationErrorPayload,
): JsonLdValidationErrorEvent => {
  return payload;
};

/**
 * Sends an event payload to GTM for further processing.
 * To be added: Allows for server-side Facebook Conversion API triggering.
 */
const analytics = (eventPayload: AnalyticsEventPayload) => {
  let payload = {};

  switch (eventPayload.event) {
    case 'view_cart':
      payload = getViewCartPayload(eventPayload as ViewCartPayload);
      break;
    case 'cart_update':
      payload = getUpdateCartPayload(eventPayload as ViewCartPayload);
      break;
    case 'view_item_list':
      payload = getViewItemListPayload(eventPayload as ViewItemListPayload);
      break;
    case 'add_to_cart':
      payload = getAddToCardPayload(eventPayload as AddToCartPayload);
      break;
    case 'view_item':
    case 'select_item':
      payload = getItemPayload(eventPayload as ItemPayload);
      break;
    case 'Hits Viewed':
      payload = getHitsViewedPayload(eventPayload as HitsViewedPayload);
      break;
    case 'jsonld_validation_error':
      payload = getJsonLdValidationPayload(eventPayload as JsonLdValidationErrorPayload);
      break;
    default:
      payload = eventPayload;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export default analytics;
